import React, { useState } from "react";
import "./TopGuidedNavigation2.scss";

import SideProgressBar1 from "./SideProgressBar1";
import TopProgressBar1 from "./TopProgressBar1";

interface FlowItem {
  id: number | string;
  name: string;
  url?: string;
  childs?: FlowItem[];
}

interface Props {
  tree: FlowItem[];
  progressbar?: boolean;
  progressbarType?: string;
  freenavigation?: string;
  onItemClick?: (item: FlowItem) => void;
}

const progressBarSkins: Record<string, React.ComponentType<any>> = {
  progressbarside: SideProgressBar1,
  progressbartop1: TopProgressBar1,
};

const TopGuidedNavigation2: React.FC<Props> = ({
  tree,
  progressbar,
  progressbarType,
  freenavigation,
  onItemClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentChildIndex, setCurrentChildIndex] = useState(-1);
  const [currentGrandChildIndex, setCurrentGrandChildIndex] = useState(-1);
  const [completed, setCompleted] = useState<(number | string)[]>([]);

  const currentItem = tree[currentIndex];

  const shouldShowProgress =
    progressbar && freenavigation === "natural";

  const CurrentProgress =
    progressBarSkins[progressbarType || ""];

  const triggerClick = (item: FlowItem | undefined) => {
    if (!item) return;
    onItemClick?.(item);
  };

  const isItemCompleted = (item: FlowItem): boolean => {
    if (!item.childs || item.childs.length === 0) {
      return completed.includes(item.id);
    }

    return item.childs.every((child) =>
      isItemCompleted(child)
    );
  };

  const getMainStatusUpdated = (
    item: FlowItem,
    index: number
  ) => {
    if (isItemCompleted(item)) return "completed";
    if (index === currentIndex) return "current";
    return "pending";
  };

  const handleNavClick = (
    item: FlowItem,
    index: number
  ) => {
    const status = getMainStatusUpdated(item, index);
    if (status === "pending") return;

    setCurrentIndex(index);
    setCurrentChildIndex(-1);
    setCurrentGrandChildIndex(-1);

    triggerClick(item);
  };

  const handleDropdownClick = (
    child: FlowItem,
    childIndex: number,
    grandChild?: FlowItem,
    grandIndex?: number
  ) => {
    const mainStatus = getMainStatusUpdated(
      tree[currentIndex],
      currentIndex
    );

    if (mainStatus === "pending") return;

    setCurrentChildIndex(childIndex);

    if (grandChild && grandIndex !== undefined) {
      setCurrentGrandChildIndex(grandIndex);
      triggerClick(grandChild);
    } else {
      setCurrentGrandChildIndex(-1);
      triggerClick(child);
    }
  };

  const handleNext = () => {
    if (!currentItem) return;

    const hasChilds =
      currentItem.childs &&
      currentItem.childs.length > 0;

    if (hasChilds && currentChildIndex === -1) {
      setCurrentChildIndex(0);
      setCurrentGrandChildIndex(-1);
      triggerClick(currentItem.childs![0]);
      return;
    }

    if (hasChilds && currentChildIndex >= 0) {
      const currentChild =
        currentItem.childs![currentChildIndex];

      const hasGrandChilds =
        currentChild.childs &&
        currentChild.childs.length > 0;

      if (
        hasGrandChilds &&
        currentGrandChildIndex <
          currentChild.childs!.length - 1
      ) {
        const nextGrand =
          currentChild.childs![
            currentGrandChildIndex + 1
          ];

        setCurrentGrandChildIndex((p) => p + 1);
        triggerClick(nextGrand);
        return;
      }

      if (hasGrandChilds && currentGrandChildIndex >= 0) {
        const lastGrand =
          currentChild.childs![currentGrandChildIndex];

        setCompleted((prev) => [...prev, lastGrand.id]);
      }

      setCompleted((prev) => [...prev, currentChild.id]);

      if (
        currentChildIndex <
        currentItem.childs!.length - 1
      ) {
        const nextChild =
          currentItem.childs![currentChildIndex + 1];

        setCurrentChildIndex((p) => p + 1);
        setCurrentGrandChildIndex(-1);
        triggerClick(nextChild);
        return;
      }

      if (!completed.includes(currentItem.id)) {
        setCompleted((prev) => [...prev, currentItem.id]);
      }

      if (tree[currentIndex + 1]) {
        const nextMain = tree[currentIndex + 1];

        setCurrentIndex((p) => p + 1);
        setCurrentChildIndex(-1);
        setCurrentGrandChildIndex(-1);
        triggerClick(nextMain);
      }

      return;
    }

    if (!completed.includes(currentItem.id)) {
      setCompleted((prev) => [...prev, currentItem.id]);
    }

    if (tree[currentIndex + 1]) {
      const nextMain = tree[currentIndex + 1];

      setCurrentIndex((p) => p + 1);
      setCurrentChildIndex(-1);
      setCurrentGrandChildIndex(-1);
      triggerClick(nextMain);
    }
  };

  const handlePrevious = () => {
    if (!currentItem) return;

    if (currentGrandChildIndex > 0) {
      const prevGrand =
        currentItem.childs![currentChildIndex].childs![
          currentGrandChildIndex - 1
        ];

      setCurrentGrandChildIndex((p) => p - 1);
      triggerClick(prevGrand);
      return;
    }

    if (currentChildIndex > 0) {
      const prevChild =
        currentItem.childs![currentChildIndex - 1];

      setCurrentChildIndex((p) => p - 1);
      setCurrentGrandChildIndex(-1);
      triggerClick(prevChild);
      return;
    }

    if (currentIndex > 0) {
      const prevMain = tree[currentIndex - 1];

      setCurrentIndex((p) => p - 1);
      setCurrentChildIndex(-1);
      setCurrentGrandChildIndex(-1);
      triggerClick(prevMain);
    }
  };

  return (
    <div className="tpg2-flow-top-guided">
      {shouldShowProgress && CurrentProgress && (
        <CurrentProgress
          tree={tree}
          currentIndex={currentIndex}
          currentChildIndex={currentChildIndex}
          currentGrandChildIndex={currentGrandChildIndex}
          completed={completed}
          isItTop={true}
        />
      )}

      <div className="tpg2-flow-top-nav">
        {tree.map((item, index) => {
          const hasDropdown =
            item.childs &&
            item.childs.length > 0;

          const status =
            getMainStatusUpdated(item, index);

          const isDisabled =
            status === "pending";

          return (
            <div
              key={item.id}
              className={`tpg2-flow-step tpg2-${status} ${
                isDisabled ? "tpg2-disabled" : ""
              }`}
            >
              <div
                className="tpg2-step-main"
                onClick={() =>
                  handleNavClick(item, index)
                }
              >
                <div className="tpg2-step-circle">
                  {index + 1}
                </div>

                <div className="tpg2-step-text">
                  <div className="tpg2-step-label">
                    {item.name}
                  </div>

                  <div className="tpg2-step-status">
                    {status}
                  </div>
                </div>
              </div>

              {hasDropdown && (
                <div className="tpg2-step-underline-wrapper">
                  <div className="tpg2-step-line" />
                  <div className="tpg2-step-arrow">
                    ▾
                  </div>
                </div>
              )}

              {hasDropdown && (
                <div className="tpg2-dropdown-panel">
                  {item.childs!.map((child, childIndex) => {
                    const hasGrandChilds =
                      child.childs &&
                      child.childs.length > 0;

                    const childIsCurrent =
                      childIndex === currentChildIndex &&
                      index === currentIndex;

                    const childStatus = completed.includes(child.id)
                      ? "completed"
                      : childIsCurrent
                      ? "current"
                      : "pending";

                    return (
                      <div
                        key={child.id}
                        className={`tpg2-dropdown-item tpg2-${childStatus} ${
                          hasGrandChilds
                            ? "tpg2-dropdown-item-main"
                            : ""
                        }`}
                        onClick={() =>
                          handleDropdownClick(
                            child,
                            childIndex
                          )
                        }
                      >
                        {child.name}

                        {hasGrandChilds && (
                          <span className="tpg2-arrow-right">
                            ▶
                          </span>
                        )}

                        {hasGrandChilds && childIsCurrent && (
                          <div className="tpg2-dropdown-panel-right">
                            {child.childs!.map(
                              (grandChild, grandIndex) => {
                                const grandIsCurrent =
                                  grandIndex ===
                                    currentGrandChildIndex &&
                                  childIndex ===
                                    currentChildIndex &&
                                  index === currentIndex;

                                const grandStatus =
                                  completed.includes(grandChild.id)
                                    ? "completed"
                                    : grandIsCurrent
                                    ? "current"
                                    : "pending";

                                return (
                                  <div
                                    key={grandChild.id}
                                    className={`tpg2-dropdown-item tpg2-${grandStatus}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDropdownClick(
                                        child,
                                        childIndex,
                                        grandChild,
                                        grandIndex
                                      );
                                    }}
                                    style={{
                                      cursor:
                                        grandStatus === "pending"
                                          ? "not-allowed"
                                          : "pointer",
                                      pointerEvents:
                                        grandStatus === "pending"
                                          ? "none"
                                          : "auto",
                                    }}
                                  >
                                    {grandChild.name}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="tpg2-flow-next-wrapper"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <button
          className="tpg2-flow-btn tpg2-flow-prev-btn"
          onClick={handlePrevious}
        >
          Previous
        </button>

        <button
          className="tpg2-flow-btn tpg2-flow-next-btn"
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TopGuidedNavigation2;